// Presentation only. The backend owns bot activity and exchange authority.
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const PREPARATION_WINDOW_MS = 3 * 60 * 60 * 1000;

// The calendar supplies next_open, including holidays, early closes and DST.
// Compare instants only; the browser's clock and timezone are not market authority.
export const preparationWindow = (bot) => {
  const asOf = bot?.responseAsOf;
  const nextOpen = bot?.market?.next_open;
  if (typeof asOf !== 'string' || typeof nextOpen !== 'string'
      || !INSTANT.test(asOf) || !INSTANT.test(nextOpen)) return null;
  const remaining = Date.parse(nextOpen) - Date.parse(asOf);
  if (!Number.isFinite(remaining) || remaining < 0) return null;
  return remaining <= PREPARATION_WINDOW_MS;
};

export const activityMarketConsistent = (bot) => {
  const code = bot?.activity?.code;
  const phase = bot?.market?.phase;
  if (code === 'waiting') return ['closed', 'non_session', 'pre_open'].includes(phase);
  if (code === 'connected') return ['open', 'mixed', 'unavailable'].includes(phase);
  return true;
};

// A fresh response alone cannot prove that a connected worker is still present.
// The server's connected/waiting projection is backed by a 45-second lease.
export const workerPresenceFresh = (bot) => {
  if (bot?.activity?.worker_connected !== true) return false;
  const asOf = Date.parse(bot.responseAsOf);
  const observed = Date.parse(bot.activity.observed_at);
  return Number.isFinite(asOf) && Number.isFinite(observed)
    && observed <= asOf && asOf - observed <= 45_000;
};

export const botPresenceState = (bot, safe) => {
  if (!safe || !bot?.activity || !activityMarketConsistent(bot)) {
    return { kind: 'unavailable', note: 'Current bot activity cannot be confirmed.', motion: false };
  }

  const { code } = bot.activity;
  if (['waiting', 'connected'].includes(code) && !workerPresenceFresh(bot)) {
    return { kind: 'unavailable', note: 'A current worker heartbeat cannot be confirmed.', motion: false };
  }
  const phase = bot.market?.phase;
  switch (code) {
    case 'waiting':
      if (phase === 'closed' || phase === 'non_session') {
        return { kind: 'sleeping', note: 'The worker is connected. The exchange is closed.', motion: true };
      }
      if (phase === 'pre_open') {
        const withinWindow = preparationWindow(bot);
        if (withinWindow === null) {
          return { kind: 'unavailable', note: 'The next eligible market opening cannot be confirmed.', motion: false };
        }
        if (!withinWindow) {
          return { kind: 'sleeping', note: 'The worker is connected. The exchange is closed until the next eligible opening.', motion: true };
        }
        return { kind: 'waking', note: 'The worker is connected. The exchange has not opened yet.', motion: true };
      }
      return { kind: 'unavailable', note: 'Market phase and worker state do not agree.', motion: false };
    case 'connected':
      if (phase === 'open') {
        return { kind: 'monitoring', note: 'The worker is connected. No evaluation is confirmed in progress.', motion: true };
      }
      if (phase === 'mixed') {
        return { kind: 'monitoring', note: 'Assigned exchanges have different session states. The worker is connected and watching for the next check.', motion: true };
      }
      return { kind: 'standby', note: 'The worker is connected. Market hours cannot be confirmed for this bot.', motion: true };
    case 'preparing':
      return { kind: 'preparing', note: bot.activity.animated === true
        ? 'Getting ready for work — a market-data preparation job has a fresh heartbeat.'
        : 'Preparation is recorded, but an active job heartbeat is unconfirmed.', motion: bot.activity.animated === true };
    case 'evaluation_started':
      return { kind: 'evaluation_started', note: 'Evaluation start recorded; continued processing is unconfirmed.', motion: false };
    case 'evaluation_running':
      return bot.activity.worker_connected === true && bot.activity.execution?.status === 'running'
        ? { kind: 'working', note: 'Fresh worker progress is recorded for this check.', motion: true }
        : { kind: 'unavailable', note: 'Current evaluation progress cannot be confirmed.', motion: false };
    case 'paused':
      return { kind: 'paused', note: 'Practice checks are paused.', motion: false };
    case 'needs_attention':
      return { kind: 'fault', note: 'Review the bot report for its recorded problem.', motion: false };
    case 'connection_lost':
    case 'preparation_delayed':
      return { kind: 'offline', note: 'A fresh worker or preparation heartbeat is unavailable.', motion: false };
    case 'archived':
      return { kind: 'archived', note: 'This bot is archived.', motion: false };
    case 'draft':
    case 'preparation_queued':
    case 'stopped':
      return { kind: 'inactive', note: 'Practice checks are not confirmed as active.', motion: false };
    default:
      return { kind: 'unavailable', note: 'Current bot activity cannot be confirmed.', motion: false };
  }
};

// The response and event timestamps are both server-owned. Device wall-clock skew cannot
// turn an old evaluation attempt into a fresh animated acknowledgement.
export const evaluationAcknowledgementKey = (bot, safe) => {
  if (!safe || bot?.activity?.code !== 'evaluation_started') return null;
  const asOf = Date.parse(bot.responseAsOf);
  const observed = Date.parse(bot.activity.observed_at);
  const age = asOf - observed;
  if (!Number.isFinite(age) || age < 0 || age > 15000) return null;
  return `${bot.bot_id}:${bot.activity.observed_at}`;
};

export const distinctActivityEvents = (items) => {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.filter((item) => {
    if (!item || typeof item.id !== 'string' || !item.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};
